import { AlertCircle, Check, Loader2, LockKeyhole, MessageSquareText, RotateCcw } from 'lucide-react'
import { Fragment, useId, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Switch } from '@/components/ui/switch'
import type { NotificationPreferencesController, PreferenceSaveState } from '@/hooks/useNotificationPreferences'
import { cn } from '@/lib/utils'
import {
  type CommunicationPreferenceEntry,
  type CommunicationPreferences,
  type LearnerVisibleNotificationPurpose,
  type NotificationPreferenceChannelState,
  type OutboundPreferenceChannel,
  parseCommunicationPreferences,
} from '@/types/notificationPreferences'

type PreferenceRow = {
  readonly purpose: LearnerVisibleNotificationPurpose
  readonly declinable: boolean
  readonly email: CommunicationPreferenceEntry
  readonly sms: CommunicationPreferenceEntry
}

const CHANNELS: readonly OutboundPreferenceChannel[] = ['email', 'sms']

/** Group the server ordered matrix without owning a second purpose policy. */
export function preferenceRows(contract: CommunicationPreferences): readonly PreferenceRow[] | null {
  const parsed = parseCommunicationPreferences(contract)
  if (!parsed) return null
  const grouped = new Map<
    LearnerVisibleNotificationPurpose,
    Partial<Record<OutboundPreferenceChannel, CommunicationPreferenceEntry>>
  >()

  for (const entry of parsed.preferences) {
    if (entry.channel !== 'email' && entry.channel !== 'sms') continue
    const row = grouped.get(entry.purpose) ?? {}
    row[entry.channel] = entry
    grouped.set(entry.purpose, row)
  }

  const rows: PreferenceRow[] = []
  for (const [purpose, entries] of grouped) {
    if (!entries.email || !entries.sms) return null
    rows.push({
      purpose,
      declinable: entries.email.declinable,
      email: entries.email,
      sms: entries.sms,
    })
  }
  return rows.length > 0 ? rows : null
}

function channelMap(
  contract: CommunicationPreferences
): Readonly<Record<OutboundPreferenceChannel, NotificationPreferenceChannelState>> | null {
  const parsed = parseCommunicationPreferences(contract)
  if (!parsed) return null
  const email = parsed.channels.find((channel) => channel.channel === 'email')
  const sms = parsed.channels.find((channel) => channel.channel === 'sms')
  return email && sms ? { email, sms } : null
}

function DirectionIsolatedText({ text, value }: { readonly text: string; readonly value: string | null }) {
  if (!value || !text.includes(value)) return text
  const parts = text.split(value)
  return parts.map((part, index) => (
    <Fragment key={`${part}:${index}`}>
      {part}
      {index < parts.length - 1 && <bdi dir='ltr'>{value}</bdi>}
    </Fragment>
  ))
}

function ChannelAddress({ state }: { readonly state: NotificationPreferenceChannelState }) {
  const { t } = useTranslation('notificationPreferences')
  return (
    <span className='block min-w-0 break-all text-[11px] leading-tight font-normal text-muted-foreground' dir='ltr'>
      {state.address ?? t('notificationPreferences.channel.noAddressShort')}
    </span>
  )
}

function BlockedChannelNotice({ state }: { readonly state: NotificationPreferenceChannelState }) {
  const { t } = useTranslation('notificationPreferences')
  if (state.usable || !state.blocked_reason) return null

  const channelName = t(`notificationPreferences.channel.${state.channel}`)
  const key = `notificationPreferences.blocked.${state.blocked_reason}`
  const hasStartNumber = state.blocked_reason === 'learner_stopped' && state.start_keyword_number
  const isolatedValue = hasStartNumber ? state.start_keyword_number : state.address
  const detail = hasStartNumber
    ? t(`${key}.withNumber`, { number: state.start_keyword_number })
    : t(`${key}.detail`, { address: state.address })

  return (
    <Alert className='border-amber-200 bg-amber-50 text-amber-950'>
      <AlertCircle aria-hidden='true' />
      <AlertTitle>{t(`${key}.title`, { channel: channelName })}</AlertTitle>
      <AlertDescription>
        <p>
          <DirectionIsolatedText text={detail} value={isolatedValue} />
        </p>
      </AlertDescription>
    </Alert>
  )
}

function ChannelStatus({
  state,
  channel,
  onRetry,
}: {
  readonly state: PreferenceSaveState | undefined
  readonly channel: OutboundPreferenceChannel
  readonly onRetry: () => void
}) {
  const { t } = useTranslation('notificationPreferences')
  if (!state) return null
  const channelName = t(`notificationPreferences.channel.${channel}`)

  if (state.state === 'error') {
    return (
      <span className='flex flex-wrap items-center gap-x-2 text-xs text-destructive' role='alert'>
        <span>{t('notificationPreferences.status.error', { channel: channelName })}</span>
        <button type='button' onClick={onRetry} className='font-semibold text-accent-purple hover:underline'>
          {t('notificationPreferences.status.retry')}
        </button>
      </span>
    )
  }

  return (
    <span className='flex items-center gap-1 text-xs text-muted-foreground' aria-live='polite'>
      {state.state === 'saving' ? (
        <Loader2 className='size-3 animate-spin' aria-hidden='true' />
      ) : (
        <Check className='size-3 text-emerald-700' aria-hidden='true' />
      )}
      {t(`notificationPreferences.status.${state.state}`, { channel: channelName })}
    </span>
  )
}

function ChannelRow({
  state,
  enabled,
  saveState,
  onChange,
  onRetry,
}: {
  readonly state: NotificationPreferenceChannelState
  readonly enabled: boolean
  readonly saveState: PreferenceSaveState | undefined
  readonly onChange: (enabled: boolean) => void
  readonly onRetry: () => void
}) {
  const { t } = useTranslation('notificationPreferences')
  const id = useId()

  return (
    <div className='flex flex-col gap-2 border-t px-3 py-4 first:border-t-0 sm:flex-row sm:items-start sm:gap-6 sm:px-4'>
      <div className='min-w-0 sm:w-48 sm:shrink-0'>
        <span id={`${id}-label`} className='text-sm font-semibold'>
          {t(`notificationPreferences.channelToggle.${state.channel}`)}
        </span>
        <ChannelAddress state={state} />
      </div>
      <div className='flex min-w-0 items-start gap-3'>
        <Switch
          checked={enabled}
          disabled={!state.usable}
          aria-busy={saveState?.state === 'saving'}
          onCheckedChange={onChange}
          aria-labelledby={`${id}-label`}
          aria-describedby={`${id}-description`}
        />
        <div className='min-w-0 space-y-1'>
          <p id={`${id}-description`} className='text-sm leading-relaxed text-gray-600'>
            {t('notificationPreferences.channelToggle.description')}
          </p>
          {!state.usable && (
            <span className='block text-xs text-muted-foreground'>
              {t(`notificationPreferences.blocked.short.${state.blocked_reason ?? 'unavailable'}`)}
            </span>
          )}
          <ChannelStatus state={saveState} channel={state.channel} onRetry={onRetry} />
        </div>
      </div>
    </div>
  )
}

function MandatoryCell({ state }: { readonly state: NotificationPreferenceChannelState }) {
  const { t } = useTranslation('notificationPreferences')
  return (
    <td className='flex justify-center'>
      <span
        className={cn(
          'rounded-full px-2 py-0.5 text-center text-[11px] leading-tight font-semibold',
          state.usable ? 'bg-indigo-50 text-indigo-950' : 'bg-muted text-muted-foreground'
        )}
      >
        {state.usable
          ? t('notificationPreferences.alwaysOn')
          : t(`notificationPreferences.blocked.short.${state.blocked_reason ?? 'unavailable'}`)}
      </span>
    </td>
  )
}

const MANDATORY_GRID =
  'grid grid-cols-[minmax(0,1fr)_5rem] items-center gap-1 px-3 sm:grid-cols-[minmax(0,1fr)_8rem] sm:px-4'

function MandatoryRow({
  row,
  email,
}: {
  readonly row: PreferenceRow
  readonly email: NotificationPreferenceChannelState
}) {
  const { t } = useTranslation('notificationPreferences')
  return (
    <tr className={cn(MANDATORY_GRID, 'border-t py-3 first:border-t-0')}>
      <th className='min-w-0 pe-1 text-start font-normal' scope='row'>
        <span className='text-sm font-semibold'>{t(`notificationPreferences.purposes.${row.purpose}.label`)}</span>
        <p className='mt-0.5 text-xs leading-relaxed text-muted-foreground'>
          {t(`notificationPreferences.purposes.${row.purpose}.description`)}
        </p>
      </th>
      <MandatoryCell state={email} />
    </tr>
  )
}

export function NotificationPreferences({ controller }: { readonly controller: NotificationPreferencesController }) {
  const { t } = useTranslation('notificationPreferences')
  const { preferences, isLoading, loadError, reload, saveStates, channelEnabled, setChannelEnabled, retry } = controller
  const rows = useMemo(() => (preferences ? preferenceRows(preferences) : null), [preferences])
  const channels = useMemo(() => (preferences ? channelMap(preferences) : null), [preferences])

  if (isLoading && !preferences) {
    return (
      <div className='flex min-h-48 items-center justify-center gap-2 text-sm text-muted-foreground' role='status'>
        <Loader2 className='size-4 animate-spin' aria-hidden='true' />
        {t('notificationPreferences.loading')}
      </div>
    )
  }

  if (loadError && !preferences) {
    return (
      <div className='space-y-4'>
        <Alert variant='destructive'>
          <AlertCircle aria-hidden='true' />
          <AlertTitle>{t('notificationPreferences.loadError.title')}</AlertTitle>
          <AlertDescription>{t('notificationPreferences.loadError.detail')}</AlertDescription>
        </Alert>
        <Button type='button' variant='outline' onClick={() => void reload()}>
          <RotateCcw aria-hidden='true' />
          {t('notificationPreferences.loadError.retry')}
        </Button>
      </div>
    )
  }

  if (!preferences || !rows || !channels) {
    return (
      <Alert>
        <AlertCircle aria-hidden='true' />
        <AlertTitle>{t('notificationPreferences.empty.title')}</AlertTitle>
        <AlertDescription>{t('notificationPreferences.empty.detail')}</AlertDescription>
      </Alert>
    )
  }

  const mandatoryRows = rows.filter((row) => !row.declinable)

  return (
    <div className='max-w-3xl space-y-5 text-start'>
      <div>
        <h2 className='mb-2 text-2xl font-bold text-gray-900'>{t('notificationPreferences.title')}</h2>
        <p className='text-sm leading-relaxed text-gray-600'>{t('notificationPreferences.description')}</p>
        <p className='mt-1 flex items-center gap-1.5 text-sm text-gray-600'>
          <MessageSquareText className='size-4 shrink-0' aria-hidden='true' />
          {t('notificationPreferences.inAppAlways')}
        </p>
      </div>

      {CHANNELS.some((channel) => !channels[channel].usable) && (
        <div className='space-y-2'>
          {CHANNELS.map((channel) => (
            <BlockedChannelNotice key={channel} state={channels[channel]} />
          ))}
        </div>
      )}

      <div className='overflow-hidden rounded-lg border bg-white'>
        {CHANNELS.map((channel) => (
          <ChannelRow
            key={channel}
            state={channels[channel]}
            enabled={channelEnabled(channel)}
            saveState={saveStates[channel]}
            onChange={(enabled) => setChannelEnabled(channel, enabled)}
            onRetry={() => retry(channel)}
          />
        ))}
      </div>

      <div className='overflow-hidden rounded-lg border bg-white'>
        <table className='w-full' aria-label={t('notificationPreferences.alwaysSent')}>
          <thead>
            <tr className={cn(MANDATORY_GRID, 'border-b bg-slate-50 py-3')}>
              <th
                className='flex min-w-0 items-center gap-2 text-start text-xs font-semibold text-muted-foreground'
                scope='col'
              >
                <LockKeyhole className='size-3.5 shrink-0' aria-hidden='true' />
                {t('notificationPreferences.alwaysSent')}
              </th>
              <th className='text-center text-xs font-bold' scope='col'>
                {t('notificationPreferences.channel.email')}
              </th>
            </tr>
          </thead>
          <tbody>
            {mandatoryRows.map((row) => (
              <MandatoryRow key={row.purpose} row={row} email={channels.email} />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
